import React, { useState, useEffect } from 'react';
import { storage } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

function FaqForm({ faq, categories, onSave, onCancel }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [order, setOrder] = useState(100);
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (faq) {
      setQuestion(faq.question || '');
      setAnswer(faq.answer || '');
      setSelectedCategories(faq.categories || []);
      setOrder(faq.order || 100);
      setImageUrl(faq.imageUrl || '');
    } else {
      setQuestion('');
      setAnswer('');
      setSelectedCategories([]);
      setOrder(100);
      setImage(null);
      setImageUrl('');
    }
  }, [faq]);

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setSelectedCategories(prev => 
      checked ? [...prev, value] : prev.filter(cat => cat !== value)
    );
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!image) return;
    setUploading(true);
    const storageRef = ref(storage, `faq_images/${image.name}`);
    try {
      await uploadBytes(storageRef, image);
      const url = await getDownloadURL(storageRef);
      setImageUrl(url);
      alert('画像が正常にアップロードされました！');
    } catch (error) {
      console.error("Error uploading image:", error);
      alert('画像のアップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      id: faq ? faq.id : null,
      question,
      answer,
      categories: selectedCategories,
      order: Number(order),
      imageUrl
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow">
      <div className="mb-4">
        <label htmlFor="question" className="block text-sm font-medium text-gray-700">質問</label>
        <input 
          type="text" 
          id="question"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="answer" className="block text-sm font-medium text-gray-700">回答（Markdown対応）</label>
        <textarea 
          id="answer"
          rows="6"
          value={answer}
          onChange={e => setAnswer(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          required
        ></textarea>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">カテゴリ</label>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
          {categories.map(cat => (
            <label key={cat.id} className="inline-flex items-center">
              <input 
                type="checkbox" 
                value={cat.name}
                checked={selectedCategories.includes(cat.name)}
                onChange={handleCategoryChange}
                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-600">{cat.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="order" className="block text-sm font-medium text-gray-700">表示順</label>
        <input 
          type="number"
          id="order"
          value={order}
          onChange={e => setOrder(e.target.value)}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">画像</label>
        <div className="mt-1 flex items-center">
          <input 
            type="file" 
            onChange={handleImageChange} 
            className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <button 
            type="button" 
            onClick={handleImageUpload} 
            disabled={!image || uploading}
            className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {uploading ? 'アップロード中...' : 'アップロード'}
          </button>
        </div>
        {imageUrl && <p className="mt-2 text-sm text-gray-500">現在の画像: <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600">表示</a></p>}
      </div>

      <div className="flex justify-end space-x-4">
        {faq && <button type="button" onClick={onCancel} className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">キャンセル</button>}
        <button type="submit" className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">FAQを保存</button>
      </div>
    </form>
  );
}

export default FaqForm;
